<template>
  <UIPopup
    v-model="modelValue"
    :closeable="false"
    hide-buttons
    full-screen
    :max-width="isDesktop ? '1130px' : isTablet ? '553px' : ''"
  >
    <template #header>
      <div class="mx-auto flex max-w-[416px] flex-col items-center justify-center gap-5 desktop:mt-24">
        <UIIcon name="alert-triangle-filled" class="!size-[64px] text-error-100" />
        <h1 class="text-center text-2xl text-black-100">{{ $t('payment.failed.title') }}</h1>
        <span class="text-center text-base font-semibold">{{ $t('payment.failed.subtitle') }}</span>
        <div v-if="packageDetails" class="w-full rounded-lg bg-error-10 text-center">
          <p class="text-sm font-medium text-black-100">
            {{ packageDetails }}
          </p>
        </div>
        <div
          v-if="!isLoadingDeclineStatus && hasMonthlyAlternative"
          class="w-full max-w-md rounded-lg bg-error-10"
        >
          <p class="text-center text-sm font-medium text-black-100">
            {{ $t('payment.failed.yearlyDeclined', { amount: formatPrice(declineStatus?.originalPackage?.price) }) }}
          </p>
          <p class="mt-2 text-center text-sm text-black-80">
            {{ $t('payment.failed.switchToMonthly', { amount: formatPrice(declineStatus?.monthlyPackage?.price) }) }}
          </p>
        </div>
      </div>
    </template>
    <template #body>
      <div class="mx-auto max-w-[488px] px-4">
        <p class="mb-8 text-center text-black-80">{{ $t('payment.failed.message') }}</p>
        <div class="mx-auto flex max-w-md flex-col gap-3">
          <UIButton
            v-if="hasMonthlyAlternative"
            id="switch-to-monthly"
            class="w-full"
            :loading="isSwitchingToMonthly"
            @click="switchToMonthly"
          >
            {{ $t('payment.failed.switchButton') }}
          </UIButton>
          <UIButton
            id="try-again"
            class="w-full"
            :variant="hasMonthlyAlternative ? 'secondary' : 'primary'"
            @click="showPaymentPopup"
          >
            {{ hasMonthlyAlternative ? $t('payment.failed.tryAnotherCard') : $t('payment.failed.tryAgainButton') }}
          </UIButton>
        </div>
        <p class="mt-4 text-center text-sm tablet:text-base">
          {{ $t('payment.failed.support.question') }}
          <NuxtLink
            no-prefetch
            class="!underline"
            to="https://api.whatsapp.com/send/?phone=%2B971527033506&text=I%20want%20to%20get%20assistance%20to%20complete%20DIC%20membership%20payment&type=phone_number&app_absent=0"
            target="_blank"
          >
            {{ $t('payment.failed.support.whatsapp') }}
            <UIIcon name="whatsapp-fill" class="ml-1" />
          </NuxtLink>
        </p>
      </div>
    </template>
  </UIPopup>
</template>

<script setup lang="ts">
import { POPUPS } from '../dynamic/maps'
import { useDeclineStatus } from '~/api/queries/useDeclineStatus'
import useCreatePackagePaymentLink from '~/api/mutations/payment/useCreatePackagePaymentLink'

const modelValue = defineModel<boolean>()

const uiStore = useUIStore()
const localePath = useLocalePath()

const { isTablet, isDesktop } = useAppBreakpoints()

const { data: declineStatus, isLoading: isLoadingDeclineStatus } = useDeclineStatus()
const { data: attemptedPackage } = useAttemptedPackage()

const hasMonthlyAlternative = computed(() => {
  if (isLoadingDeclineStatus.value) return false
  
  const isDeclined = declineStatus.value?.isDeclined
  const hasMonthly = declineStatus.value?.monthlyPackage
  const hasOriginal = declineStatus.value?.originalPackage
  const isYearly = declineStatus.value?.originalPackage?.billingPeriod?.toLowerCase() === 'yearly'
  
  return isDeclined && hasMonthly && hasOriginal && isYearly
})

const packageDetails = computed(() => {
  if (!attemptedPackage.value) return null

  const tier = attemptedPackage.value.tier
    ? attemptedPackage.value.tier.charAt(0).toUpperCase() + attemptedPackage.value.tier.slice(1)
    : ''
  const billing = attemptedPackage.value.billingPeriod
    ? attemptedPackage.value.billingPeriod.charAt(0).toUpperCase() + attemptedPackage.value.billingPeriod.slice(1)
    : ''

  if (tier && billing) {
    return `Package: ${tier} (${billing})`
  } else if (tier) {
    return `${attemptedPackage.value.name} - ${tier}`
  }
  return attemptedPackage.value.name
})

const formatPrice = (price: number | null | undefined): string => {
  if (!price) return ''
  return `$${price}`
}

const showPaymentPopup = () => {
  modelValue.value = false
  nextTick(() => {
    uiStore.showPopup(POPUPS.UPGRADE_MODAL)
  })
}

const isSwitchingToMonthly = ref(false)
const { mutateAsync: createPaymentLink } = useCreatePackagePaymentLink()

const switchToMonthly = async () => {
  if (!declineStatus.value?.monthlyPackage?.id) return

  try {
    isSwitchingToMonthly.value = true
    await createPaymentLink(declineStatus.value.monthlyPackage.id)
  } catch (error) {
    console.error('Error switching to monthly package:', error)
    const uiStore = useUIStore()
    uiStore.showSnackBanner('error', $t('payment.failed.switchError'))
  } finally {
    isSwitchingToMonthly.value = false
  }
}

const { mutateAsync: sendPaymentFailedNotification } = useSendPaymentFailedNotification()

onMounted(() => {
  sendPaymentFailedNotification().catch(() => {})
})
</script>
